// SPDX-License-Identifier: AGPL-3.0-or-later

const VAR = '--avatar-costume-scroll';
/** px/ms that maps to a clamped |speed| of 1. */
const REF_SPEED = 4.5;
const ATTACK = 0.45;
/** Envelope leak time constant. After ~1s of inertia, ~15% squash remains. */
const TAU_MS = 540;
const DT_MIN_MS = 8;
const DT_MAX_MS = 48;
const SETTLE = 0.012;
const FLIP_MIN = 0.2;
const JUMP_PX = 400;
const WHEEL_OWNS_MS = 80;
/**
 * Steps per unit for the written value. `--avatar-costume-scroll` is inherited and
 * registered, so every distinct value invalidates style for the whole scroller subtree
 * and forces each costume's drop-shadow to re-rasterize. Writing raw floats made that
 * happen on every frame of a flick and its ~1s momentum tail. A step of 0.01 moves the
 * squash by at most 1% of the 0.28 scale factor, under a pixel at avatar sizes, and lets
 * the decay tail reuse one value across frames instead of minting a new one each time.
 */
const WRITE_STEPS = 100;

/**
 * Marks the scroll root while the squash is running, so costume art can drop its halo for the
 * duration. Each haloed costume carries two stacked `drop-shadow` filters, and the squash changes
 * its `scaleY` every frame, so the filter output cannot be reused between frames: WebKit re-runs
 * both blur passes per costume per frame for the whole flick and its momentum tail. A phone with
 * several dozen costumed avatars in the list is rendering that many filter surfaces continuously,
 * which is GPU-backed memory that never shows up in a JS heap measurement.
 *
 * The halo exists so low-contrast art does not sink into the chat surface while you read it. Mid
 * flick there is nothing to read, and it comes back as soon as the squash settles.
 */
const SCROLLING_ATTR = 'data-avatar-costume-scrolling';

const SCROLL_ROOT_SELECTOR = '[data-fluxer-scroll-container="true"]';

type RootBinding = {
	count: number;
	release: () => void;
};

const bindings = new WeakMap<HTMLElement, RootBinding>();

function createAvatarCostumeScrollDriver(): {
	onWheel: (target: HTMLElement, deltaY: number, deltaMode?: number) => void;
	onScroll: (target: HTMLElement, scrollTop: number) => void;
	dispose: () => void;
} {
	let envelope = 0;
	let shown = 0;
	let raf = 0;
	let lastAdvance = 0;
	let lastSampleAt = 0;
	let wheelOwnsUntil = 0;
	let lastTop: number | null = null;
	let el: HTMLElement | null = null;
	let written: string | null = null;

	const write = () => {
		if (el == null) return;
		if (Math.abs(shown) < SETTLE && Math.abs(envelope) < SETTLE) {
			shown = 0;
			envelope = 0;
			if (written != null) {
				el.style.removeProperty(VAR);
				el.removeAttribute(SCROLLING_ATTR);
				written = null;
			}
			return;
		}
		const next = String(Math.round(shown * WRITE_STEPS) / WRITE_STEPS);
		if (next === written) return;
		if (written == null) {
			el.setAttribute(SCROLLING_ATTR, '');
		}
		el.style.setProperty(VAR, next);
		written = next;
	};

	const leakTo = (now: number) => {
		if (lastAdvance === 0) {
			lastAdvance = now;
			return;
		}
		const dt = now - lastAdvance;
		lastAdvance = now;
		if (dt <= 0 || envelope === 0) return;
		envelope *= Math.exp(-dt / TAU_MS);
		if (Math.abs(envelope) < SETTLE) envelope = 0;
	};

	const easeShown = () => {
		if (envelope !== 0 && shown !== 0 && Math.sign(envelope) !== Math.sign(shown)) {
			shown += (envelope - shown) * ATTACK;
			return;
		}
		if (Math.abs(envelope) > Math.abs(shown)) {
			shown += (envelope - shown) * ATTACK;
			return;
		}
		shown = envelope;
	};

	const tick = () => {
		raf = 0;
		leakTo(performance.now());
		easeShown();
		write();
		if (Math.abs(shown) >= SETTLE || Math.abs(envelope) >= SETTLE) raf = requestAnimationFrame(tick);
	};

	const kick = (nextEl: HTMLElement, delta: number) => {
		if (delta === 0 || !Number.isFinite(delta)) return;
		if (document.documentElement.classList.contains('reduced-motion')) return;
		const now = performance.now();
		const sampleDt = Math.max(DT_MIN_MS, Math.min(DT_MAX_MS, lastSampleAt === 0 ? DT_MAX_MS : now - lastSampleAt));
		lastSampleAt = now;
		if (el !== nextEl) {
			el?.removeAttribute(SCROLLING_ATTR);
			written = null;
		}
		el = nextEl;
		leakTo(now);
		const speed = Math.max(-1, Math.min(1, delta / sampleDt / REF_SPEED));
		if (envelope === 0) {
			envelope = speed;
		} else if (Math.sign(speed) !== Math.sign(envelope)) {
			if (Math.abs(speed) >= FLIP_MIN) envelope = speed;
		} else if (Math.abs(speed) > Math.abs(envelope)) {
			envelope = speed;
		}
		easeShown();
		write();
		if (raf === 0) raf = requestAnimationFrame(tick);
	};

	return {
		onWheel(nextEl, deltaY, deltaMode = 0) {
			const px = deltaMode === 1 ? deltaY * 16 : deltaMode === 2 ? deltaY * window.innerHeight : deltaY;
			wheelOwnsUntil = performance.now() + WHEEL_OWNS_MS;
			kick(nextEl, px);
		},
		onScroll(nextEl, scrollTop) {
			if (lastTop == null) {
				lastTop = scrollTop;
				return;
			}
			const delta = scrollTop - lastTop;
			lastTop = scrollTop;
			if (performance.now() < wheelOwnsUntil || Math.abs(delta) > JUMP_PX) return;
			kick(nextEl, delta);
		},
		dispose() {
			if (raf !== 0) cancelAnimationFrame(raf);
			raf = 0;
			if (el != null) {
				el.style.removeProperty(VAR);
				el.removeAttribute(SCROLLING_ATTR);
			}
			el = null;
			written = null;
			lastTop = null;
			lastAdvance = 0;
			lastSampleAt = 0;
			wheelOwnsUntil = 0;
			envelope = 0;
			shown = 0;
		},
	};
}

/** One driver per Fluxer scroller, retained by mounted costumes. */
export function retainAvatarCostumeScrollFrom(from: Element): () => void {
	const root = from.closest<HTMLElement>(SCROLL_ROOT_SELECTOR);
	if (root == null) return () => {};

	let binding = bindings.get(root);
	if (binding == null) {
		const driver = createAvatarCostumeScrollDriver();
		const onScroll = () => {
			driver.onScroll(root, root.scrollTop);
		};
		const onWheel = (event: WheelEvent) => {
			driver.onWheel(root, event.deltaY, event.deltaMode);
		};
		root.addEventListener('scroll', onScroll, {passive: true});
		root.addEventListener('wheel', onWheel, {passive: true});
		binding = {
			count: 0,
			release: () => {
				root.removeEventListener('scroll', onScroll);
				root.removeEventListener('wheel', onWheel);
				driver.dispose();
				bindings.delete(root);
			},
		};
		bindings.set(root, binding);
	}
	binding.count += 1;
	let released = false;
	return () => {
		if (released) return;
		released = true;
		binding.count -= 1;
		if (binding.count === 0) binding.release();
	};
}
